<template>
    <div id="lt-yantr" class="advanced-calc auto-affordability-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <ul class="filter-inputs four-col mob-ai-baseline">
                    <li>
                        <number-input
                            v-model="MonthlyPayment"
                            :label="labelMonthlyPayment"
                            inputmode="decimal"
                            @input="calculate();"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="DownPayment"
                            :label="labelDownPayment"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="TradeIn"
                            :label="labelTradeIn"
                            :tooltip-link="tooltipTradeIn"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="AmountOwed"
                            :label="labelAmountOwed"
                            :tooltip-link="tooltipAmountOwed"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <drop-down
                            v-model="CreditScore"
                            :label="labelCreditScore"
                            :options="CreditScoreList"
                        />
                        <a href="/redirect/offers?id=calc-fcs" class="font-sm">Get your credit score</a>
                    </li>
                    <li>
                        <number-input
                            v-model="InterestRate"
                            :label="labelInterestRate"
                            symbol="%"
                            symbol-aligned="right"
                            precision="3"
                            max-value="100.000"
                            inputmode="decimal"
                            @input="validateInterestRate"
                        />
                    </li>
                    <li>
                        <drop-down
                            v-model="LoanTermInYears"
                            :label="labelLoanTerm"
                            :options="LoanTermInYearsList"
                        />
                    </li>
                </ul>
            </div>
            <div class="row calculation-section">
                <div class="col-xs-12 col-sm-7 left-section">
                    <h4>You could afford a car worth</h4>
                    <label class="homeprice">{{ currencyWholeNumber(PurchasePrice) }}</label>
                    <!-- Range Slider start-->
                    <div class="slider-parent">
                        <vue-slider
                            :value="slider.value"
                            :height="slider.height"
                            :process-style="slider.processStyle"
                            :tooltip="slider.tooltip"
                            :slider-style="slider.sliderStyle"
                            :bg-style="slider.bgStyle"
                            :dot-size="slider.dotSize"
                            :max="slider.max"
                            :min="slider.min"
                            :clickable="slider.clickable"
                            @callback="formatPurchasePrice($event)"
                        />
                        <div class="row range-label">
                            <div class="col-xs-6 text-left">
                                <label>Conservative</label>
                            </div>
                            <div class="col-xs-6 text-right">
                                <label>Aggressive</label>
                            </div>
                        </div>
                    </div>
                    <!-- Range Slider end-->
                    <ul class="calc-box">
                        <li>
                            <label>Monthly Payment</label>
                            <h3 class="value">{{ currencyWholeNumber(CalculatedMoPayment) }}</h3>
                        </li>
                        <li>
                            <label>Total down payment</label>
                            <h3 class="value">{{ currencyWholeNumber(TotalDownPayment) }}</h3>
                            <lt-label :tooltip-link="tooltipDownPayment" />
                        </li>
                        <li>
                            <label>Loan amount</label>
                            <h3 id="loan-amount" class="value">{{ currencyWholeNumber(LoanAmount) }}</h3>
                            <lt-label :tooltip-link="tooltipLoanAmount" />
                        </li>
                    </ul>
                    <div class="view-free-offers">
                        <h4>We’ve found offers for this result:</h4>
                        <button class="btn btn-blue" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                    </div>
                    <div>
                        <advertising-disclosures
                            v-if="DisclosureText"
                            :disclosure-text="DisclosureText"
                            :disclosure-tokens="DisclosureTokens"
                        />
                    </div>
                    <lt-logo v-if="ltLogo==1">
                        <small>NMLS#1136 Terms & Conditions Apply</small>
                    </lt-logo>
                </div>
                <div class="col-xs-12 col-sm-5 right-section text-center">
                    <h4>Total Loan Breakdown</h4>
                    <div class="chart-parent">
                        <vue-chart
                            id="donutChart"
                            type="doughnut"
                            :data="chartData"
                            :options="options"
                            :width="chartWidth"
                            :height="chartHeight"
                        />
                        <div class="donut-inner">
                            <label>Your Total Payments:</label>
                            <h3 :class="{ 'red-text': !isNegativeAmount}">{{ currencyWholeNumber(TotalOfAllPayments) }}</h3>
                        </div>
                    </div>
                    <div class="monthly-budget">
                        <ul class="list-unstyled summary">
                            <li>
                                <label><span class="green-bg"></span> Purchase Price:</label>
                                <span class="value">{{ currencyWholeNumber(LoanAmount) }}</span>
                            </li>
                            <li>
                                <label><span class="purple-bg"></span> Total interest:</label>
                                <span class="value">{{ currencyWholeNumber(TotalInterest) }}</span>
                            </li>
                        </ul>
                        <div class="clear-fix"></div>
                    </div>
                </div>
                <div v-if="howToUseCalculator==1" class="col-xs-12 about-calculator text-center">
                    <p class="sub-heading">Our auto affordability calculator helps you understand how much car you can afford based on your information you filled.</p>
                    <a v-vue-scroll-to="{ el:'#how-to-use-auto-affordability-calculator', offset: -90 }">How to use this calculator</a>
                </div>
            </div>
            <div class="full-width-border"></div>
            <auto-amortization-chart
                v-if="amortizationChartSection==1"
                :loan-amount="LoanAmount"
                :loan-term-in-years="LoanTermInYears"
                :interest-rate="InterestRate"
            />
            <auto-amortization-table
                v-if="amortizationTableSection==1"
                :loan-amount="LoanAmount"
                :loan-term-in-years="LoanTermInYears"
                :interest-rate="InterestRate"
            />
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import vueSlider from 'vue-slider-component';
    import Accounting from 'accounting';
    import VueScrollTo from 'vue-scrollto';
    import AutoAmortizationChart from '~/components/auto-amortization-chart.vue';
    import AutoAmortizationTable from '~/components/auto-amortization-table.vue';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import LoanTermList from '~/constants/loan-term-list';
    import CreditScoreList from '~/constants/credit-score-list';
    import LtLabel from '~/components/lt-label.vue';
    import ChartTooltip from '~/helpers/chart-tooltip';
    import BaseMixin from '~/mixin/base-mixin';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';

    export default {
        components: {
            NumberInput,
            DropDown,
            LtLabel,
            VueChart,
            vueSlider,
            AutoAmortizationChart,
            AutoAmortizationTable,
            LtLogo,
            AdvertisingDisclosures,
        },
        directives: {
            VueScrollTo,
        },
        mixins: [BaseMixin],
        props: {
            filterHeader: {
                default: 'Auto Information',
            },
            labelMonthlyPayment: {
                default: 'Desired Monthly Payment:',
            },
            labelDownPayment: {
                default: 'Down Payment:',
            },
            labelTradeIn: {
                default: 'Trade-In Value:',
            },
            labelAmountOwed: {
                default: 'Amnt. Owed On Trade:',
            },
            labelCreditScore: {
                default: 'Credit Score:',
            },
            labelInterestRate: {
                default: 'Interest Rate:',
            },
            labelLoanTerm: {
                default: 'Loan Term:',
            },
            monthlyPayment: {
                default: 450,
            },
            downPayment: {
                default: 6000,
            },
            tradeIn: {
                default: 3000,
            },
            amountOwed: {
                default: 0,
            },
            interestRate: {
                default: 1.250,
            },
            creditScore: {
                default: '1',
            },
            loanTermInYears: {
                default: 5,
            },
            span: {
                type: String,
                default: '',
            },
            maxValue: {
                default: 80,
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '',
            },
            disclosureText: {
                default: '',
            },
            mortgagePaymentSection: {
                default: 1,
            },
            amortizationTableSection: {
                default: 1,
            },
            amortizationChartSection: {
                default: 1,
            },
            allTotalPaymentSection: {
                default: 1,
            },
            howToUseCalculator: {
                default: 1,
            },
            totalMoPayment: {
                default: 478.26,
            },
            totalPayment: {
                default: 60,
            },
            totalInterest: {
                default: 840,
            },
            tooltipAmountOwed: {
                default: 'amount-owed-on-trade',
            },
            tooltipTradeIn: {
                default: 'net-trade-in-value',
            },
            tooltipDownPayment: {
                default: 'total-down-payment',
            },
            tooltipLoanAmount: {
                default: 'loan-amount',
            },
            destinationLinkOrId: {
                default: 'LT-auto-calc',
            },
            destinationButtonText: {
                default: 'View Free Offers',
            },
            loanAmount: {
                default: 0,
            },
        },
        data() {
            return {
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.autoloanterm,
                CreditScoreList: CreditScoreList.autoloan,
                TotalPayment: this.totalPayment,
                Registration: this.registration,
                DownPayment: this.downPayment,
                AmountOwed: this.amountOwed,
                CreditScore: this.creditScore,
                MonthlyPayment: this.monthlyPayment,
                TradeIn: this.tradeIn,
                TotalInterest: this.totalInterest,
                InterestRate: this.interestRate,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                TotalDownPayment: 0,
                PurchasePrice: 0,
                TotalOfAllPayments: 0,
                IsSliderEvent: false,
                CalculatedMoPayment: 0,
                // Setting for chart
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#08c177',
                                '#2087c1',
                            ],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 89,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                // Setting for range slider
                slider: {
                    value: 50,
                    height: 10,
                    sliderStyle: {
                        backgroundColor: '#00aeef',
                    },
                    bgStyle: {
                        background: 'linear-gradient(to right, rgb(8, 193, 119) 50%, rgb(250, 157, 12) 60%, rgb(219, 53, 81) 97%)',
                    },
                    processStyle: {
                        backgroundColor: 'transparent',
                    },
                    dotSize: 25,
                    tooltip: false,
                    clickable: false,
                    max: 100,
                    min: 1,
                },
                chartWidth: 200,
                chartHeight: 130,
                CurrentDate: new Date(),
            };
        },
        computed: {
            isNegativeAmount() {
                return this.totalMoPayment >= 0;
            },
            LoanAmount() {
                return this.CalculatedMoPayment * (1 - (1 + this.InterestRate / 1200) ** -this.TotalPayment) / (this.InterestRate / 1200);
            },
            getFieldValues() {
                return this.DownPayment + this.TradeIn + this.AmountOwed + this.InterestRate + this.LoanTermInYears + this.MonthlyPayment + this.CalculatedMoPayment;
            },
        },
        watch: {
            LoanTermInYears() {
                this.TotalPayment = this.LoanTermInYears * 12;
            },
            InterestRate() {
                this.validateInterestRate();
            },
            getFieldValues() {
                this.calculate();
            },
            MonthlyPayment() {
                this.MonthlyPayment = this.checkFieldValue(this.MonthlyPayment);
                this.CalculatedMoPayment = this.unformatFigure(this.MonthlyPayment);
            },
            PurchasePrice() {
                if (!this.IsSliderEvent) {
                    this.updateSlider();
                }
                this.changeColor();
                this.IsSliderEvent = false;
            },
            TradeIn() {
                this.TradeIn = this.checkFieldValue(this.TradeIn);
            },
            AmountOwed() {
                this.AmountOwed = this.checkFieldValue(this.AmountOwed);
            },
            DownPayment() {
                this.DownPayment = this.checkFieldValue(this.DownPayment);
            },
        },
        mounted() {
            this.calculate();
            this.updateSlider();
            this.changeColor();
            this.CalculatedMoPayment = this.MonthlyPayment;
        },
        methods: {
            capitalize(value) {
                if (!value) return '';
                return value.toString().charAt(0).toUpperCase() + value.toString().slice(1);
            },
            currencyWholeNumber(figure) {
                return figure > 0 ? Accounting.formatMoney(figure, { precision: 0 }) : 0;
            },
            absolute(figure) {
                return Accounting.formatMoney(Math.abs(figure), { precision: 0 });
            },
            calculate() {
                this.chartData.labels = [];
                this.chartData.datasets[0].data = [];
                this.TotalDownPayment = this.unformatFigure(this.DownPayment) + this.unformatFigure(this.TradeIn) - this.unformatFigure(this.AmountOwed);
                this.TotalInterest = this.CalculatedMoPayment * this.TotalPayment - this.LoanAmount;
                this.PurchasePrice = this.LoanAmount + this.TotalDownPayment;
                this.TotalOfAllPayments = this.LoanAmount + this.TotalInterest;
                this.chartData.datasets[0].data.push((this.LoanAmount / this.TotalOfAllPayments * 100).toFixed(2));
                this.chartData.datasets[0].data.push((this.TotalInterest / this.TotalOfAllPayments * 100).toFixed(2));
                this.chartData.labels.push(`Purchase Price ${Accounting.formatMoney(this.LoanAmount, { precision: 0 })}`);
                this.chartData.labels.push(`Total interest ${Accounting.formatMoney(this.TotalInterest, { precision: 0 })}`);
            },
            formatPurchasePrice(val) {
                this.IsSliderEvent = true;
                this.PurchasePrice = val;
                this.CalculatedMoPayment = this.InterestRate / 1200 * (val - this.TotalDownPayment) / (1 - (1 + this.InterestRate / 1200) ** -this.TotalPayment);
            },
            updateSlider() {
                const purchasePrice = this.checkValue(this.PurchasePrice);
                const diffvalue = Math.round(purchasePrice > 0 ? purchasePrice : 1) * 20 / 100;
                this.slider.value = purchasePrice;
                this.slider.min = Math.round(purchasePrice) - diffvalue;
                this.slider.max = Math.round(purchasePrice) + diffvalue;
            },
            validateInterestRate() {
                if (this.InterestRate <= 0) {
                    this.InterestRate = 0;
                } else if (this.InterestRate >= 100) {
                    this.InterestRate = 100;
                }
            },
            changeColor() {
                let hue = this.slider.value / (this.PurchasePrice > 0 ? this.PurchasePrice : this.slider.value) * 100;
                hue = hue >= 100 ? hue : hue - 60;
                const lightness = hue < 100 ? 50 : 34;
                document.getElementById('loan-amount').style.color = ['hsl(', hue, ',91%,', lightness, '%)'].join('');
            },
            checkValue(value) {
                return value > 0 ? value : 0;
            },
            checkFieldValue(value) {
                return this.unformatFigure(value) > 0 ? value : 0;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
